import moment from "moment";
import { degrees, PDFDocument, PDFFont, PDFPage, rgb } from "pdf-lib";
import {
  bottom,
  center,
  DynamicContent,
  ImageContent,
  left,
  middle,
  right,
  StaticContent,
} from "../constants";
import { PageContent, PreviewImage, TemplateContent } from "../types";
import { drawMargin } from "./drawMargin";
import {
  fetchImage,
  getXPosition,
  getYPosition,
  sanitizeAndConvertText,
  wrapText,
} from "./helpers";

export const drawTemplateOnPage = async (
  pdfDoc: PDFDocument,
  page: PDFPage,
  templateData: TemplateContent,
  font: PDFFont,
  isBOM?: boolean,
  data?: PageContent,
  previewImage?: PreviewImage
) => {
  try {
    // Get the width and height of the first page
    const { width, height } = page.getSize();

    let top_offset =
      height - ((templateData?.margin?.top_offset || 5) * height) / 100;
    let width_offset =
      width - ((templateData?.margin?.width_offset || 5) * width) / 100;
    let bottom_offset =
      ((templateData?.margin?.bottom_offset || 5) * height) / 100;

    drawMargin(
      page,
      {
        ...(templateData?.margin || {}),
        top_offset: top_offset,
        width_offset: width_offset,
        bottom_offset: bottom_offset,
      },
      { width, height }
    );

    let startXPosition = getXPosition(
      width,
      templateData?.margin?.width_offset || 5
    );
    let startYPosition = getYPosition(
      height,
      templateData?.margin?.top_offset || 5
    );
    let startYPositionBottom = getYPosition(
      height,
      templateData?.margin?.bottom_offset || 5
    );

    let contentLeftPosition = startXPosition + 1;
    let contentCenterPosition = (width - startXPosition) / 2 - 20;
    let contentRightPosition = (contentSize: number) =>
      startXPosition +
      width -
      (2 * (templateData?.margin?.width_offset || 5) * width) / 100 -
      contentSize;

    let contentTopPosition = height - 10;
    let contentMiddlePosition = height - startYPosition / 2 - 5;
    let contentBottomPosition = height - startYPosition + 5;

    let attachmentUrl = templateData?.attachments?.[templateData?.attachments?.length - 1]?.url;

    const logoConfig = templateData?.headers?.find(
      (header) => header?.content?.content_type === ImageContent
    );

    // Logo Dimension

    let logoWidth = (logoConfig?.content?.width || 60) * 0.75;
    let logoHeight = (logoConfig?.content?.height || 60) * 0.75;

    // Logo Alignment

    let horizontal = logoConfig?.alignment?.horizontal;
    let vertical = logoConfig?.alignment?.vertical;

    if (previewImage && previewImage?.isActive) {
      attachmentUrl = previewImage?.url;
      logoWidth = (previewImage?.dimension?.width || 60) * 0.75;
      logoHeight = (previewImage?.dimension?.height || 60) * 0.75;
      horizontal = previewImage?.alignment?.horizontal || left;
      vertical = previewImage?.alignment?.vertical || middle;
    }

    let logoContent = null;

    let logoUrl = attachmentUrl || "";
    await fetchImage(logoUrl)
      .then(async (logoBytes) => {
        logoContent = logoUrl?.includes("png")
          ? await pdfDoc.embedPng(logoBytes)
          : await pdfDoc.embedJpg(logoBytes);
      })
      .catch(async (error: any) => {
        console.error(`Error loading logo on preview: ${error}`);
      });

    if (logoContent && (logoConfig?.is_active || previewImage?.isActive)) {
      let logoTopPos = height - logoHeight;
      let logoBottomPos = height - startYPosition + 1;
      let logoMiddlePos =
        height -
        (((templateData?.margin?.top_offset || 5) / 2) * height) / 100 -
        logoHeight / 2;

      let logoX = contentLeftPosition;
      if (horizontal === right) {
        logoX = contentRightPosition(logoWidth);
      } else if (horizontal === center) {
        logoX = contentCenterPosition;
      }

      let logoY = logoTopPos;
      if (vertical === middle) {
        logoY = logoMiddlePos;
      } else if (vertical === bottom) {
        logoY = logoBottomPos;
      }

      page.drawImage(logoContent, {
        x: logoX,
        y: logoY,
        width: logoWidth,
        height: logoHeight,
      });
    }

    if (templateData?.is_header_active) {
      templateData?.headers
        ?.filter(
          (_) => _?.content?.content_type === StaticContent && _?.is_active
        )
        .forEach((header: any) => {
          const textSize = 10;

          const lines = wrapText(
            sanitizeAndConvertText(header?.content?.text),
            (width -
              2 * (((templateData?.margin?.width_offset || 5) * width) / 100)) /
              3,
            textSize,
            font
          );

          let staticX = contentLeftPosition;
          if (header?.alignment?.horizontal === center) {
            staticX = contentCenterPosition;
          } else if (header?.alignment?.horizontal === right) {
            staticX = contentRightPosition(
              font.widthOfTextAtSize(sanitizeAndConvertText(lines[0]), textSize)
            );
          }

          let staticY = contentTopPosition;
          if (header?.alignment?.vertical === middle) {
            staticY = contentMiddlePosition;
          } else if (header?.alignment?.vertical === bottom) {
            staticY = contentBottomPosition;
          }

          const lineHeight = 1.5;

          let currentY = staticY;
          for (const line of lines) {
            if (height - currentY >= startYPosition) {
              // Reached the maximum height, stop rendering
              break;
            }
            page.drawText(line, {
              x: staticX,
              y: currentY,
              size: textSize,
              font,
            });
            currentY -= font.heightAtSize(textSize * lineHeight);
          }
        });
    }

    const footerTextSize = 7;
    const footerLineHeight = 1.5;

    let footerTopPosition = startYPositionBottom - 10;
    let footerBottomPosition = 5;
    let footerMiddlePosition = (footerTopPosition + footerBottomPosition) / 2;

    // Footers
    const getFooterX = (horizontal: string, fontSize: number, text: string) => {
      let footerX =
        startXPosition +
        width -
        (2 * (templateData?.margin?.width_offset || 5) * width) / 100 -
        font.widthOfTextAtSize(text, fontSize);
      if (horizontal === left) {
        footerX = contentLeftPosition;
      } else if (horizontal === center) {
        footerX = (contentLeftPosition + footerX) / 2;
      }
      return footerX;
    };

    const getFooterY = (vertical: string) => {
      let footerY = footerTopPosition;
      if (vertical === middle) {
        footerY = footerMiddlePosition;
      } else if (vertical === bottom) {
        footerY = footerBottomPosition;
      }
      return footerY;
    };

    let pagination = templateData?.page_number;

    if (pagination?.print) {
      page.drawText(pagination?.format, {
        x: getFooterX(
          pagination?.alignment?.horizontal,
          footerTextSize,
          pagination?.format
        ),
        y: getFooterY(pagination?.alignment?.vertical),
        size: footerTextSize,
      });
    }

    // Branding
    if (templateData?.branding) {
      const branding = templateData?.branding;
      const text = `Generated using IEHUB on ${moment(new Date()).format(
        "ll"
      )}`;
      let currentY = getFooterY(branding?.alignment?.vertical);
      page.drawText(text, {
        x: getFooterX(branding?.alignment?.horizontal, footerTextSize, text),
        y: currentY,
        size: footerTextSize,
        font,
        color: rgb(0.5, 0.5, 0.5),
      });
      currentY -= font.heightAtSize(footerTextSize * footerLineHeight);
    }

    if (templateData?.is_footer_active) {
      templateData?.footers
        ?.filter(
          (_: any) => _?.content?.content_type === StaticContent && _?.is_active
        )
        ?.forEach((footer: any) => {
          const lines = wrapText(
            sanitizeAndConvertText(footer?.content?.text),
            (width -
              2 * (((templateData?.margin?.width_offset || 5) * width) / 100)) /
              3,
            footerTextSize,
            font
          );

          let currentY = getFooterY(footer?.alignment?.vertical);
          for (const line of lines) {
            if (currentY >= height - startYPositionBottom) {
              // Reached the maximum height, stop rendering
              break;
            }
            page.drawText(line, {
              x: getFooterX(
                footer?.alignment?.horizontal,
                footerTextSize,
                sanitizeAndConvertText(lines[0])
              ),
              y: currentY,
              size: footerTextSize,
              font,
              color: rgb(0.5, 0.5, 0.5),
            });
            currentY -= font.heightAtSize(footerTextSize * footerLineHeight);
          }
        });
    }

    // Left Annotation
    const leftTextSize = 7;

    let leftTopPosition = 10;
    let leftBottomPosition = startXPosition - 5;
    let leftMiddlePosition = (leftTopPosition + leftBottomPosition) / 2;

    const left_annotation = templateData?.left_annotation;
    if (left_annotation && left_annotation?.is_active) {
      const lines = wrapText(
        sanitizeAndConvertText(left_annotation?.content?.text),
        height - (startYPosition + startYPositionBottom),
        leftTextSize,
        font
      );

      // X position calculation
      let leftX = leftTopPosition;
      if (left_annotation?.alignment?.horizontal === center) {
        leftX = leftMiddlePosition;
      } else if (left_annotation?.alignment?.horizontal === right) {
        leftX = leftBottomPosition;
      }

      // Y position calculation
      let verticalLeftTop =
        height -
        ((templateData?.margin?.top_offset || 5) * height) / 100 -
        font.widthOfTextAtSize(sanitizeAndConvertText(lines[0]), leftTextSize);
      let verticalLeftBottom = startYPositionBottom;
      let verticalLeftMiddle = (verticalLeftTop + verticalLeftBottom) / 2;

      let leftY = verticalLeftTop;
      if (left_annotation?.alignment?.vertical === middle) {
        leftY = verticalLeftMiddle;
      } else if (left_annotation?.alignment?.vertical === bottom) {
        leftY = verticalLeftBottom;
      }

      const lineHeight = 1.5;

      let currentX = leftX;
      for (const line of lines) {
        if (currentX >= startXPosition) {
          // Reached the maximum height, stop rendering
          break;
        }
        page.drawText(line, {
          x: currentX,
          y: leftY,
          size: leftTextSize,
          font,
          rotate: degrees(90),
          color: rgb(0.5, 0.5, 0.5),
        });
        currentX += font.heightAtSize(leftTextSize * lineHeight);
      }
    }

    // Right Annotation
    const right_annotation = templateData?.right_annotation;
    if (right_annotation && right_annotation?.is_active) {
      const rightTextSize = 7;

      const lines = wrapText(
        sanitizeAndConvertText(right_annotation?.content?.text),
        height - (startYPosition + startYPositionBottom),
        rightTextSize,
        font
      );

      // X position calculation
      let rightTopPosition = width - leftTopPosition;
      let rightMiddlePosition = width - leftMiddlePosition;
      let rightBottomPosition = width - leftBottomPosition;

      let rightX = rightBottomPosition;
      if (right_annotation?.alignment?.horizontal === center) {
        rightX = rightMiddlePosition;
      } else if (right_annotation?.alignment?.horizontal === right) {
        rightX = rightTopPosition;
      }

      // Y position calculation
      let verticalRightTop = height - startYPosition;
      let verticalRightBottom =
        startYPositionBottom +
        font.widthOfTextAtSize(sanitizeAndConvertText(lines[0]), rightTextSize);
      let verticalRightMiddle = (verticalRightTop + verticalRightBottom) / 2;

      let rightY = verticalRightTop;
      if (right_annotation?.alignment?.vertical === middle) {
        rightY = verticalRightMiddle;
      } else if (right_annotation?.alignment?.vertical === bottom) {
        rightY = verticalRightBottom;
      }

      const lineHeight = 1.5;

      let currentX = rightX;
      for (const line of lines) {
        if (currentX <= width - startXPosition) {
          // Reached the maximum height, stop rendering
          break;
        }
        page.drawText(line, {
          x: currentX,
          y: rightY,
          size: rightTextSize,
          font,
          rotate: degrees(-90),
          color: rgb(0.5, 0.5, 0.5),
        });
        currentX -= font.heightAtSize(rightTextSize * lineHeight);
      }
    }

    // render headers dynamic content
    templateData?.headers
      ?.filter(
        (_) => _?.content?.content_type === DynamicContent && _?.is_active
      )
      .forEach((header: any) => {
        const textSize = 10;

        let fields = [...(header?.content?.fields ?? [])];

        if (previewImage) {
          fields = fields?.map(f => ({ ...f, display_name: f?.display_name?.replace("Material", "Material/BOM") ?? "" }))
        }

        if (header?.alignment?.vertical === bottom) {
          fields = fields.reverse();
        }

        fields?.forEach(
          (
            field: { display_name: string; field_name: keyof PageContent },
            index: number
          ) => {
            try {
              const accessField =
                data && field?.field_name?.includes(".")
                  ? eval(`data.${field?.field_name}`)
                  : data
                  ? data?.[field?.field_name]
                  : "";

              let content = `${
                isBOM
                  ? field?.display_name?.replace("Material", "BOM")
                  : field?.display_name
              }: ${accessField || "-"}`;

              const actualContentHeight = font.heightAtSize(textSize);

              const textHeight = index * (font.heightAtSize(textSize) + 2);

              let staticX = contentLeftPosition;
              if (header?.alignment?.horizontal === center) {
                staticX = contentCenterPosition;
              } else if (header?.alignment?.horizontal === right) {
                staticX = contentRightPosition(
                  font.widthOfTextAtSize(content, textSize)
                );
              }

              const topHeaderPos = contentTopPosition - textHeight;
              const middleHeaderPos =
                height -
                ((height - top_offset - actualContentHeight * fields?.length) /
                  2 +
                  (1 + (fields?.length - 1))) -
                textHeight;
              const bottomHeaderPos =
                height - (height - top_offset) + textHeight + 5;

              let staticY = topHeaderPos;
              if (header?.alignment?.vertical === middle) {
                staticY = middleHeaderPos;
              } else if (header?.alignment?.vertical === bottom) {
                staticY = bottomHeaderPos;
              }

              page.drawText(content, {
                x: staticX,
                y: staticY,
                size: textSize,
              });
            } catch (error) {
              console.error(`Error accessing cutsheet header values: ${error}`);
            }
          }
        );
      });

    return page;
  } catch (error: any) {
    console.error(`Error rendering template: ${error}`);
  }
};
